function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable class-methods-use-this */

import Log from '@deephaven/log';
import { CanceledPromiseError, PromiseUtils } from '@deephaven/utils';
import { FileUtils } from '@deephaven/file-explorer';
import debounce from 'lodash.debounce';
var log = Log.module('GrpcFileStorageTable');

/**
 * Implementation of FileStorageTable for gRPC service.
 * Takes a path to specify what root this table should start at.
 */
export class GrpcFileStorageTable {
  /**
   * @param storageService The storage service to use
   * @param baseRoot Base root for the service
   * @param root The root path for this storage table
   */
  constructor(storageService) {
    var baseRoot = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    var root = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : baseRoot;
    _defineProperty(this, "storageService", void 0);
    _defineProperty(this, "baseRoot", void 0);
    _defineProperty(this, "root", void 0);
    _defineProperty(this, "currentSize", 0);
    _defineProperty(this, "currentViewport", void 0);
    _defineProperty(this, "listeners", []);
    _defineProperty(this, "viewportUpdatePromise", void 0);
    _defineProperty(this, "currentViewportData", void 0);
    /**
     * Map of expanded directory paths to the tables that manage that path.
     * We use a tree of tables to query the server so we just get the directories that are expanded.
     */
    _defineProperty(this, "childTables", new Map());
    this.storageService = storageService;
    this.baseRoot = baseRoot;
    this.root = root;
    if (root === baseRoot) {
      this.doRefresh = debounce(this.doRefresh.bind(this), GrpcFileStorageTable.REFRESH_DEBOUNCE);
    }
  }
  removeBaseRoot(filename) {
    return FileUtils.removeRoot(this.baseRoot, filename);
  }
  getViewportData() {
    var _this$viewportUpdateP;
    if (!this.viewportUpdatePromise) {
      this.refreshInternal();
    }
    return (_this$viewportUpdateP = this.viewportUpdatePromise) !== null && _this$viewportUpdateP !== void 0 ? _this$viewportUpdateP : Promise.resolve({
      items: [],
      offset: 0
    });
  }
  getSnapshot(sortedRanges) {
    throw new Error('Method not implemented.');
  }
  get size() {
    return this.currentSize;
  }
  setExpanded(path, expanded) {
    var paths = path.split('/');
    var nextPath = paths.shift();
    if (nextPath === undefined || nextPath === '') {
      nextPath = paths.shift();
    }
    if (nextPath === undefined || nextPath === '') {
      throw new Error("Invalid path: ".concat(path));
    }
    var remainingPath = paths.join('/');
    if (expanded) {
      if (!this.childTables.has(nextPath)) {
        var childTable = new GrpcFileStorageTable(this.storageService, this.baseRoot, "".concat(this.root, "/").concat(nextPath));
        this.childTables.set(nextPath, childTable);
      }
      if (remainingPath) {
        var _childTable = this.childTables.get(nextPath);
        _childTable === null || _childTable === void 0 || _childTable.setExpanded(remainingPath, expanded);
      }
    } else if (this.childTables.has(nextPath)) {
      if (remainingPath) {
        var _childTable2 = this.childTables.get(nextPath);
        _childTable2 === null || _childTable2 === void 0 || _childTable2.setExpanded(remainingPath, expanded);
      } else {
        this.childTables.delete(nextPath);
      }
    }
    if (this.currentViewport) {
      this.refreshInternal();
    }
  }
  collapseAll() {
    this.childTables.clear();
    if (this.currentViewport) {
      this.refreshInternal();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  setSearch(search) {
    throw new Error('Method not implemented.');
  }
  setViewport(viewport) {
    this.currentViewport = viewport;
    this.refreshInternal();
  }
  setFilters() {
    throw new Error('Method not implemented.');
  }
  setSorts() {
    throw new Error('Method not implemented.');
  }
  setReversed() {
    throw new Error('Method not implemented.');
  }
  close() {
    this.listeners = [];
  }
  onUpdate(callback) {
    this.listeners = [...this.listeners, callback];
    return () => {
      this.listeners = this.listeners.filter(other => other !== callback);
    };
  }
  doRefresh(callback) {
    log.debug2('doRefresh', this.root);
    callback(this.fetchData());
  }
  refresh() {
    var _this$viewportUpdateP2;
    log.debug2('refresh', this.root);
    (_this$viewportUpdateP2 = this.viewportUpdatePromise) === null || _this$viewportUpdateP2 === void 0 || _this$viewportUpdateP2.cancel();
    var refreshPromise = new Promise(resolve => {
      this.doRefresh(resolve);
    });
    var viewportUpdatePromise = PromiseUtils.makeCancelable(refreshPromise.then(viewportData => {
      if (this.viewportUpdatePromise !== viewportUpdatePromise) {
        throw new CanceledPromiseError();
      }
      this.currentViewportData = viewportData;
      this.sendUpdate();
      return viewportData;
    }));

    // Add a catch here as we're not catching the promises anywhere else
    viewportUpdatePromise.catch(e => {
      if (!PromiseUtils.isCanceled(e)) {
        log.error('Error updating viewport', e);
      }
    });
    this.viewportUpdatePromise = viewportUpdatePromise;
  }

  /**
   * Refreshes data, but catches any errors and logs them
   */
  refreshInternal() {
    try {
      this.refresh();
    } catch (e) {
      if (!PromiseUtils.isCanceled(e)) {
        log.error('Unable to refresh data', e);
      }
    }
  }
  fetchData() {
    var _this = this;
    return _asyncToGenerator(function* () {
      var viewport = _this.currentViewport;
      if (!viewport) {
        throw new Error('No viewport set');
      }

      // First get the root directory contents
      var dirContents = yield _this.storageService.listItems(_this.root);
      var items = dirContents.map(file => ({
        type: file.type,
        filename: _this.removeBaseRoot(file.filename),
        basename: file.basename,
        id: _this.removeBaseRoot(file.filename),
        isExpanded: file.type === 'directory' ? _this.childTables.has(file.basename) : undefined
      })).sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === 'directory' ? -1 : 1;
        }
        return a.basename.localeCompare(b.basename);
      });

      // Get the data from all expanded directories
      for (var i = 0; i < items.length; i += 1) {
        var item = items[i];
        var basename = item.basename,
          filename = item.filename;
        if (filename === _this.removeBaseRoot("".concat(_this.root, "/").concat(basename)) && item.type === 'directory') {
          var childTable = _this.childTables.get(basename);
          if (childTable != null) {
            childTable.setViewport({
              top: 0,
              bottom: viewport.bottom - i
            });
            // eslint-disable-next-line no-await-in-loop
            var childViewportData = yield childTable.getViewportData();
            items.splice(i + 1, 0, ...childViewportData.items);
          }
        }
      }
      _this.currentSize = items.length;
      log.debug2(_this.root, 'items', items, viewport);

      // Slice it to the correct viewport
      items = items.slice(viewport.top, viewport.bottom);
      return {
        items,
        offset: viewport.top
      };
    })();
  }
  sendUpdate() {
    var _this$currentViewport;
    // Retain a reference to it in case a listener gets removed while sending an update
    var listeners = this.listeners;
    var data = (_this$currentViewport = this.currentViewportData) !== null && _this$currentViewport !== void 0 ? _this$currentViewport : {
      items: [],
      offset: 0
    };
    for (var i = 0; i < listeners.length; i += 1) {
      listeners[i](data);
    }
  }
}
_defineProperty(GrpcFileStorageTable, "REFRESH_DEBOUNCE", 50);
export default GrpcFileStorageTable;
//# sourceMappingURL=GrpcFileStorageTable.js.map